import React, { forwardRef, useEffect, useState } from "react";
import { Empty, Select } from "antd/lib";
import { Item, SearchableSelectProps } from "@/types/common";
import { useSelectsOptionsFetchers } from "@/hooks/useSelectsOptionsFetchers";
import { useTranslation } from "react-i18next";
import { LoadingOutlined } from "@ant-design/icons";
import styles from "./SearchableSelect.module.scss";
import { XStroke } from "@/public/icons";

const { Option } = Select;

const SearchableSelect: React.FC<SearchableSelectProps> = forwardRef(function SearchableSelectField(
  {
    dataType,
    placeholder,
    value,
    onChange,
    multiSelect = false,
    initialItems = [],
    disabled = false,
    filteredOptionsAreEmpty,
    setFilteredOptionsAreEmpty,
    shouldFilter = false,
    eventId,
    userId,
    ...selectProps
  },
  ref,
) {
  const { fetchSelectOptions } = useSelectsOptionsFetchers();
  const { t } = useTranslation();

  const [items, setItems] = useState<Item[]>(initialItems);
  const [searchString, setSearchString] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (val: any, option: any) => {
    if (!onChange) return;
    if (multiSelect) {
      onChange(val, option);
      return;
    }
    const selectedItem = items.find((item) => item.value === val);
    onChange(val, selectedItem ? { ...option, label: selectedItem.name } : option);
  };

  useEffect(() => {
    if (!shouldFilter || !setFilteredOptionsAreEmpty || !searchString) return;
    const hasMatch = items.some((item) => item.name.toLowerCase().indexOf(searchString.toLowerCase()) >= 0);
    setFilteredOptionsAreEmpty(!hasMatch);
  }, [searchString, items]);

  useEffect(() => {
    if (!dataType) return;

    const fetchItems = async () => {
      if (searchString.length > 2) {
        setLoading(true);

        const response = await fetchSelectOptions({ dataType, setIsLoading: setLoading, searchString, eventId, userId });

        if (shouldFilter) {
          const uniqueItemsToSet = [...response.data];

          initialItems.forEach((item) => {
            if (!uniqueItemsToSet.some((initialItem) => item.value === initialItem.value)) uniqueItemsToSet.push(item);
          });

          setItems(uniqueItemsToSet);
        } else {
          setItems(response.data);
        }

        setLoading(false);
      }
    };

    const delayDebounce = setTimeout(() => {
      fetchItems();
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [dataType, searchString]);

  return (
    <Select
      mode={multiSelect ? "multiple" : undefined}
      className="notranslate"
      showSearch
      placeholder={placeholder}
      onSearch={(value) => {
        if (!filteredOptionsAreEmpty && setFilteredOptionsAreEmpty) setFilteredOptionsAreEmpty(true);
        setSearchString(value);
      }}
      filterOption={
        shouldFilter
          ? (input, option) =>
              !!option && option.children.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          : false
      }
      notFoundContent={loading ? <p>{t("common.states.selectLoadingContent")}</p> : <Empty />}
      value={value}
      onChange={handleChange}
      loading={loading}
      disabled={disabled}
      allowClear={{
        clearIcon: loading ? (
          <LoadingOutlined spin />
        ) : (
          <div className={styles.clearIcon}>
            <XStroke color="rgba(0, 0, 0, 0.25)" />
          </div>
        ),
      }}
      {...selectProps}
    >
      {items.map((item) => (
        <Option key={item.value} value={item.value}>
          <span className="notranslate">{item.name}</span>
        </Option>
      ))}
    </Select>
  );
});

export default SearchableSelect;
